import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import {
  notifyToast,
  dismissToast,
  subscribeToasts,
  type GlobalToast,
  type GlobalToastType,
} from '../../lib/toastStore';
import {
  subscribeConfirmDialog,
  resolveCurrentDialog,
  cancelCurrentDialog,
  type ConfirmRequest,
} from '../../lib/confirmStore';

export type ToastType = GlobalToastType;

interface ToastItem {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
}

interface ToastLayerProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}

const typeStyles: Record<ToastType, { border: string; bg: string; icon: React.ElementType; iconColor: string }> = {
  success: {
    border: 'border-emerald-200',
    bg: 'bg-white',
    icon: CheckCircle,
    iconColor: 'text-emerald-500',
  },
  error: {
    border: 'border-rose-200',
    bg: 'bg-white',
    icon: AlertCircle,
    iconColor: 'text-rose-500',
  },
  warning: {
    border: 'border-amber-200',
    bg: 'bg-white',
    icon: AlertCircle,
    iconColor: 'text-amber-500',
  },
  info: {
    border: 'border-brand/20',
    bg: 'bg-white',
    icon: Info,
    iconColor: 'text-brand',
  },
};

function Toast({ item, onDismiss }: { item: ToastItem; onDismiss: (id: string) => void; key?: string }) {
  const style = typeStyles[item.type];
  const Icon = style.icon;

  const TOAST_DURATION_MS = 5000;

  React.useEffect(() => {
    const t = setTimeout(() => onDismiss(item.id), TOAST_DURATION_MS);
    return () => clearTimeout(t);
  }, [item.id, onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.96, filter: 'blur(4px)' }}
      animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
      exit={{ opacity: 0, y: 12, scale: 0.96, transition: { duration: 0.18 } }}
      transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
      className={`pointer-events-auto w-full max-w-sm mx-auto sm:mx-0 flex items-start gap-3 rounded-2xl border p-4 pr-3 shadow-lg relative overflow-hidden ${style.bg} ${style.border}`}
      role="alert"
      aria-live="polite"
    >
      {/* Auto-dismiss progress bar (Section 5.3) */}
      <span
        className="absolute bottom-0 left-0 right-0 h-0.5 toast-progress"
        style={{ backgroundColor: style.iconColor.includes('emerald') ? '#10B981' : style.iconColor.includes('rose') ? '#EF4444' : style.iconColor.includes('amber') ? '#F59E0B' : 'var(--brand-primary)' }}
        aria-hidden="true"
      />
      <Icon className={`shrink-0 mt-0.5 h-5 w-5 ${style.iconColor}`} strokeWidth={2} />
      <div className="flex-1 min-w-0 text-left">
        {item.title && <p className="text-xs font-bold text-slate-800 mb-0.5">{item.title}</p>}
        <p className="text-xs text-slate-600 leading-relaxed">{item.message}</p>
      </div>
      <button
        onClick={() => onDismiss(item.id)}
        className="shrink-0 mt-0.5 rounded-md p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
        aria-label="Dismiss notification"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </motion.div>
  );
}

export default function ToastLayer({ toasts, onDismiss }: ToastLayerProps) {
  return (
    <div className="toast-layer top-4 left-0 right-0 flex flex-col gap-3 items-center px-4">
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <Toast key={t.id} item={t} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </div>
  );
}

/**
 * Global notification layer. Mount ONCE at the application root. Renders into
 * document.body via a portal so it is never trapped inside a page layout's
 * stacking context, and sits above the modal stack, sidebars and sticky
 * headers. All roles share this single layer.
 */
export function GlobalToastLayer() {
  const [toasts, setToasts] = useState<GlobalToast[]>([]);

  useEffect(() => subscribeToasts(setToasts), []);

  return ReactDOM.createPortal(
    <ToastLayer toasts={toasts} onDismiss={dismissToast} />,
    document.body,
  );
}

/**
 * Global confirm/prompt layer. Mount ONCE at the application root beside
 * GlobalToastLayer. Renders the current confirmStore request (the
 * non-blocking replacement for window.confirm / window.prompt) into
 * document.body via a portal, above every modal and drawer.
 */
export function GlobalConfirmLayer() {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const [value, setValue] = useState('');

  useEffect(() => subscribeConfirmDialog(setRequest), []);
  useEffect(() => {
    if (request) setValue(request.defaultValue || '');
  }, [request?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!request) return null;

  const isPrompt = request.kind === 'prompt';

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 'var(--z-modal, 1000)' }}
      role="alertdialog"
      aria-modal="true"
      aria-label={request.message}
    >
      <div className="absolute inset-0 bg-slate-900/50" onClick={() => cancelCurrentDialog()} />
      <div className="relative w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl border border-slate-200 space-y-4 animate-fade-in">
        <p className="text-sm font-semibold text-slate-800 leading-relaxed">{request.message}</p>
        {isPrompt && (
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={3}
            autoFocus
            className="w-full rounded-xl border border-slate-300 focus:border-brand focus:ring-1 focus:ring-brand-primary px-3 py-2 text-sm outline-none text-slate-800"
          />
        )}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => cancelCurrentDialog()}
            className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition cursor-pointer"
          >
            {request.cancelLabel || 'Cancel'}
          </button>
          <button
            type="button"
            onClick={() => resolveCurrentDialog(isPrompt ? value : true)}
            className="px-4 py-2 rounded-xl btn-primary text-white text-xs font-bold transition cursor-pointer"
          >
            {request.confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Hook used by components that previously owned their own toast state.
 * Backed by the global store, so notifications bubble up to the single
 * root-level layer regardless of where the component renders.
 */
export function useToasts() {
  const [toasts, setToasts] = useState<GlobalToast[]>([]);

  useEffect(() => subscribeToasts(setToasts), []);

  const notify = useCallback((type: GlobalToastType, message: string, title?: string) => {
    notifyToast(type, message, title);
  }, []);

  const dismiss = useCallback((id: string) => {
    dismissToast(id);
  }, []);

  return { toasts, notify, dismiss };
}
